import { useEffect, useRef, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Button, CircularProgress, Stack } from '@mui/material';
import * as Sentry from '@sentry/react';
import { Controller, useForm } from 'react-hook-form';
import * as z from 'zod';
import { MarkdownRenderer } from '../markdown-renderer';
import { MarkdownEditor } from '../markdown-renderer/remirror';
import {
  Modes,
  defaultValidationSchema,
  type MarkdownRendererInlineEditProps,
} from './interfaces';
import { useNotification } from '@/context/notification';
import { useClickOutside } from '@/hooks/use-click-outside';

const getWindowSelection = () => {
  const selection = window.getSelection();
  return selection && selection.type === 'Range';
};

export const MarkdownRendererInlineEdit = ({
  editable,
  onSave: onSaveCallback,
  onFocus,
  onBlur,
  placeholder,
  markdown,
  validationSchema = defaultValidationSchema,
  initialMode = Modes.viewing,
}: MarkdownRendererInlineEditProps) => {
  const { toast } = useNotification();
  const [mode, setMode] = useState<Modes>(initialMode);
  const [viewingMarkdown, setViewingMarkdown] = useState<string>(markdown);
  const editingWrapperRef = useRef<HTMLDivElement | null>(null);

  const {
    control,
    formState: { isValid },
    watch,
  } = useForm({
    defaultValues: {
      editingMarkdown: markdown,
    },
    mode: 'onChange',
    resolver: zodResolver(
      z.object({
        editingMarkdown: validationSchema,
      })
    ),
  });

  useClickOutside({
    callback: () => {
      handleDiscard();
      onBlur?.();
    },
    ref: editingWrapperRef,
  });

  const handleDiscard = () => {
    if (mode !== Modes.editing) return;

    setMode(Modes.viewing);
  };

  const handleSaveStart = () => {
    setMode(Modes.pending);
  };

  const handleSaveSuccess = (value: string) => {
    setViewingMarkdown(value);
    setMode(Modes.viewing);
  };

  const handleSaveError = () => {
    setMode(Modes.editing);
    toast({
      content: 'Erro ao salvar',
      type: 'error',
    });
  };

  const handleSave = async () => {
    const markdown = watch('editingMarkdown') as string;

    if (!onSaveCallback) {
      return handleSaveSuccess(markdown);
    }

    try {
      handleSaveStart();
      await onSaveCallback(markdown);
      handleSaveSuccess(markdown);
    } catch (error) {
      handleSaveError();
      return Sentry.captureMessage(
        `Markdown Renderer Inline Edit Error: ${error}`,
        'error'
      );
    }
  };

  const handleEditMode = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    const isLink = target.tagName === 'A';
    const isSelecting = getWindowSelection();

    if (isSelecting || isLink) {
      return e.stopPropagation();
    }

    setMode(Modes.editing);
  };

  useEffect(() => {
    setViewingMarkdown(markdown);
  }, [markdown]);

  if (!editable) {
    return (
      <MarkdownRenderer markdown={viewingMarkdown} placeholder={placeholder} />
    );
  }

  if (mode === Modes.viewing) {
    return (
      <Box
        onClick={handleEditMode}
        sx={{
          '&:hover': {
            borderRadius: (theme) => theme.spacing(0),
            backgroundColor: 'neutral.100',
            cursor: 'text',
          },
        }}
      >
        <MarkdownRenderer
          markdown={viewingMarkdown}
          placeholder={placeholder}
          translatable={false}
        />
      </Box>
    );
  }

  if (mode === Modes.editing || mode === Modes.pending) {
    return (
      <Box ref={editingWrapperRef}>
        <Controller
          control={control}
          name="editingMarkdown"
          render={({ field }) => {
            return (
              <Stack spacing={2}>
                <MarkdownEditor
                  markdown={viewingMarkdown}
                  editable
                  onBlur={onBlur}
                  onChange={field.onChange}
                  onDiscard={() => handleDiscard()}
                  onFocus={onFocus}
                  onSave={() => {
                    if (isValid) handleSave();
                  }}
                  placeholder={placeholder}
                />
                <Stack direction="row" spacing={1}>
                  <Button
                    disabled={!isValid || mode === Modes.pending}
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                      e.stopPropagation();
                      handleSave();
                    }}
                    variant="contained"
                    startIcon={
                      mode === Modes.pending ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : null
                    }
                  >
                    Salvar
                  </Button>
                  <Button
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                      e.stopPropagation();
                      handleDiscard();
                    }}
                  >
                    Cancelar
                  </Button>
                </Stack>
              </Stack>
            );
          }}
        />
      </Box>
    );
  }
};
